import { SearchIcon, SyncIcon } from "@primer/octicons-react";
import { Box } from "@primer/react";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  foDiemDanhAnApi,
  IDiemDanhAnItemResponse,
} from "../../../api/foDiemDanhAnApi";
import ComboboxCrmLop from "../../../components/combobox-crmlop/ComboboxCrmLop";
import ComboboxKhoi from "../../../components/combobox-khoi/ComboboxKhoi";
import ComboboxTrangThaiDiemDanhAn from "../../../components/combobox-trang-thai-dd-an";
import { ComboboxTruong } from "../../../components/combobox-truong";
import HocSinhShortInfo from "../../../components/hoc-sinh/HocSinhShortInfo";
import Button from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table";
import { IColumn } from "../../../components/ui/data-table/DataTable";
import { default as MyText, default as Text } from "../../../components/ui/text";
import { default as MyTextInput, default as TextInput } from "../../../components/ui/text-input";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { useCaAnByTruong } from "../../../hooks/useCaAnByTruong";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { useDangKyAnHocSinh } from "../../../hooks/useDangKyAnHocSinh";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import FaceidViewModal from "./FaceidViewModal";

interface IDiemDanhAnItemExtended extends IDiemDanhAnItemResponse {
  diemDanhInfo?: {
    [fo_caan_id: number]: {
      gio_diem_danh: string;
      ghi_chu: string;
      is_synced: boolean;
      is_dang_ky_an: boolean;
      image_url: string;
    };
  };
}

type PendingItem = {
  id: number;
  ts_hocsinh_id: number;
  fo_caan_id: number;
  ngay_an: string;
  gio_diem_danh: string;
  ghi_chu: string;
  nam_hoc: string;
  image_url: string;
  app_notify_id: number;
  is_synced: boolean;
  is_dang_ky_an?: boolean;
};

type MergedCellData = {
  gio_diem_danh: string;
  ghi_chu: string;
  is_synced: boolean;
  is_dang_ky_an: boolean;
  image_url: string;
};

const BaoCaoDiemDanhPage = () => {
  const { dm_coso_id, nam_hoc } = useCommonSelectedHook();
  const { checkAccesiableTo } = useCommonContext();
  const { status } = useDangKyAnHocSinh();
  const { caAnList, loadCaAnByTruong, clearCaAnList } = useCaAnByTruong();

  const [foDiemDanhAns, setFoDiemDanhAns] =
    useState<IDiemDanhAnItemExtended[]>([]);

  const [isSearch, setIsSearch] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"all" | "diemDanh" | "hopLe" | "khongHopLe">("all");
  const [filterTruongId, setFilterTruongId] = useState<number>(0);
  const [filterKhoiId, setFilterKhoiId] = useState<number>(0);
  const [filterLopId, setFilterLopId] = useState<number>(0);
  const [tu_ngay, setTuNgay] = useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [den_ngay, setDenNgay] = useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [filterMahs, setFilterMahs] = useState('');

  const [pendingChanges] = useState<Map<string, PendingItem>>(
    () => new Map()
  );
  const [isShowViewFaceId, setShowViewFaceId] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");

  const canView = checkAccesiableTo(
    "food/bao-cao/diem-danh-an/ngay-an/{ngay_an}/nam-hoc/{nam_hoc}/co-so/{dm_coso_id}",
    "GET"
  );

  const handleChangeFromDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTuNgay(e.target.value);
  };

  const handleChangeToDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDenNgay(e.target.value);
  };

  // ✅ GROUP THEO HỌC SINH + NGÀY
  const transformDiemDanhData = (
    rawData: IDiemDanhAnItemResponse[]
  ): IDiemDanhAnItemExtended[] => {
    const grouped = new Map<string, IDiemDanhAnItemExtended>();

    for (const item of rawData) {
      const key = `${item.ts_hocsinh_id}_${item.ngay_an}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          ...item,
          diemDanhInfo: Object.create(null),
          combo_dang_ky: item.combo_dang_ky || "",
        });
      }

      const record = grouped.get(key)!;

      // ✅ map điểm danh theo bữa
      if (item.fo_caan_id) {
        record.diemDanhInfo![item.fo_caan_id] = {
          gio_diem_danh: item.gio_diem_danh || "",
          ghi_chu: item.ghi_chu || "",
          is_synced: item.is_synced || false,
          is_dang_ky_an: item.is_dang_ky_an,
          image_url: (item.image_url || item.image_url || "")
        };
      }

      // ✅ combo áp cho cả ngày (chỉ cần lấy 1 lần)
      if (!record.combo_dang_ky && item.combo_dang_ky) {
        record.combo_dang_ky = item.combo_dang_ky;
      }
    }

    return Array.from(grouped.values()).map((item) => ({
      ...item,
      combo_dang_ky:
        item.combo_dang_ky && item.combo_dang_ky.trim() !== ""
          ? item.combo_dang_ky
          : "KA",
    }));
  };

  const handleSearchUserAsync = async () => {
    if (!dm_coso_id || !nam_hoc || !tu_ngay || !den_ngay || !filterTruongId) {
      NotifyHelper.Error("Kiểm tra lại các thông tin tìm kiếm bắt buộc!");
      return;
    }

    setIsSearch(true);
    const res = await foDiemDanhAnApi.selectByNgayAnRange({
      tu_ngay,
      den_ngay,
      nam_hoc,
      dm_coso_id,
      dm_truong_id: filterTruongId,
      ma_hs: filterMahs
    });

    if (res.is_success) {
      setFoDiemDanhAns(transformDiemDanhData(res.data || []));
    }

  };

  useEffect(() => {
    setFoDiemDanhAns([]);
    setIsSearch(false);
  }, [filterTruongId, dm_coso_id, nam_hoc, tu_ngay, den_ngay, filterMahs]);

  useEffect(() => {
    if (filterTruongId) loadCaAnByTruong(filterTruongId);
    else clearCaAnList();
  }, [filterTruongId]);

  // ✅ CACHE THEO HỌC SINH + NGÀY + BỮA
  const mergedCache = useMemo<Map<string, MergedCellData>>(() => {
    const cache = new Map<string, MergedCellData>();

    foDiemDanhAns.forEach((item) => {
      const info = item.diemDanhInfo || {};

      Object.keys(info).forEach((key) => {
        const cacheKey = `${item.ts_hocsinh_id}_${item.ngay_an}_${key}`;
        const original = info[Number(key)];
        const pending = pendingChanges.get(cacheKey);

        cache.set(
          cacheKey,
          pending
            ? {
              gio_diem_danh: pending.gio_diem_danh,
              ghi_chu: pending.ghi_chu,
              is_synced: original.is_synced,
              is_dang_ky_an: original.is_dang_ky_an,
              image_url: pending.image_url ?? (original?.image_url || "")
            }
            : original
        );
      });
    });

    return cache;
  }, [foDiemDanhAns, pendingChanges]);

  const getMergedCellData = useCallback(
    (item: IDiemDanhAnItemExtended, fo_caan_id: number): MergedCellData =>
      mergedCache.get(
        `${item.ts_hocsinh_id}_${item.ngay_an}_${fo_caan_id}`
      ) || {
        gio_diem_danh: "",
        ghi_chu: "",
        is_synced: false,
        is_dang_ky_an: false,
        image_url: ""
      },
    [mergedCache]
  );

  const filteredData = useMemo(() => {
    return foDiemDanhAns.filter((item) => {
      // filter cũ
      if (filterTruongId && item.dm_truong_id !== filterTruongId) return false;
      if (filterKhoiId && item.dm_khoi_id !== filterKhoiId) return false;
      if (filterLopId && item.dm_lop_id !== filterLopId) return false;

      // ✅ FILTER THEO TRẠNG THÁI ĐIỂM DANH
      if (statusFilter === "all") return true;

      const infos = Object.values(item.diemDanhInfo || {});

      if (statusFilter === "diemDanh") {
        return infos.some(
          (i) => i.gio_diem_danh && i.gio_diem_danh.trim() !== ""
        );
      }

      if (statusFilter === "hopLe") {
        return infos.some(
          (i) =>
            i.gio_diem_danh &&
            i.gio_diem_danh.trim() !== "" &&
            i.is_dang_ky_an
        );
      }

      if (statusFilter === "khongHopLe") {
        return infos.some(
          (i) =>
            i.gio_diem_danh &&
            i.gio_diem_danh.trim() !== "" &&
            !i.is_dang_ky_an
        );
      }

      return true;
    });
  }, [
    foDiemDanhAns,
    filterTruongId,
    filterKhoiId,
    filterLopId,
    statusFilter
  ]);

  const sortedCaAn = useMemo(
    () => [...caAnList].sort((a, b) => a.fo_caan_id - b.fo_caan_id),
    [caAnList]
  );

  const dataForExport = useMemo(() => {
    return filteredData.map((row) => {
      const extra: Record<string, any> = {};

      sortedCaAn.forEach((caAn) => {
        const info = getMergedCellData(row, caAn.fo_caan_id);

        extra[`gio_diem_danh_${caAn.fo_caan_id}`] =
          info?.gio_diem_danh
            ? moment(info.gio_diem_danh, "HH:mm:ss").format("HH:mm")
            : "";

        extra[`image_url_${caAn.fo_caan_id}`] = info?.image_url || "";
      });

      return {
        ...row,
        ...extra,
      };
    });
  }, [filteredData, sortedCaAn, getMergedCellData]);


  const generateCaAnColumns: IColumn[] = useMemo(
    () =>
      sortedCaAn.flatMap((caAn) => [{
        caption: caAn.ca_an,
        alignment: "center",
        cellRender: (data: IDiemDanhAnItemExtended) => {
          const info = getMergedCellData(data, caAn.fo_caan_id);
          const isDiemDanh = !!info.gio_diem_danh?.trim();

          return (
            <Box sx={{ textAlign: "center", padding: 1 }}>
              {isDiemDanh ? (
                <>
                  <Text
                    text={moment(info.gio_diem_danh, "HH:mm:ss").format("HH:mm")}
                    sx={{ fontSize: "12px", fontWeight: 500 }}
                  />
                  <br />
                  <Box
                    sx={{
                      display: 'flex',
                      fontSize: "9px",
                      fontStyle: "italic",
                      justifyContent: 'center',
                    }}>
                    <Text
                      text={info.is_dang_ky_an ? "Hợp lệ" : "Không hợp lệ"}
                      sx={{
                        color: info.is_dang_ky_an
                          ? "success.fg"
                          : "danger.fg",
                      }}
                    /> -
                    <Box sx={{ cursor: 'pointer' }} onClick={() => {
                      setSelectedImageUrl(info.image_url)
                      setSelectedTitle(`${data.ho_ten} - ${data.ma_hs} - ${caAn.ca_an}`)
                      setShowViewFaceId(true)
                    }}> Xem ảnh</Box>
                  </Box>


                </>
              ) : (
                <Text
                  text="--:--"
                  sx={{ fontSize: "12px", color: "fg.muted" }}
                />
              )}
            </Box>
          );
        },
      },
      {
        caption: `${caAn.ca_an} - Giờ điểm danh`,
        dataField: `gio_diem_danh_${caAn.fo_caan_id}`,
        isVisible: false
      },
      {
        caption: `${caAn.ca_an} - Link ảnh`,
        dataField: `image_url_${caAn.fo_caan_id}`,
        isVisible: false
      }]),
    [sortedCaAn, getMergedCellData]
  );

  if (!canView) {
    return (
      <Box sx={{ textAlign: "center", padding: 4 }}>
        <Text text="Base.Message.NoPermission" />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", marginBottom: 2 }}>
        <Box>
          <MyText text="Từ ngày" sx={{ marginRight: 1 }} />
          <MyTextInput
            type="date"
            value={tu_ngay}
            onChange={handleChangeFromDate}
          />
        </Box>
        <Box>
          <MyText text="Đến ngày" sx={{ marginRight: 1 }} />
          <MyTextInput
            type="date"
            value={den_ngay}
            onChange={handleChangeToDate}
          />
        </Box>
        <Box>
          <ComboboxTruong
            value={filterTruongId}
            onValueChanged={setFilterTruongId}
          />
        </Box>
        <Box>
          <TextInput
            block
            value={filterMahs}
            onValueDelayChanged={(value) => setFilterMahs(value)}
            placeholder="Nhập mã học sinh"
          />
        </Box>
        <Box>
          <Button
            text="Tìm kiếm"
            variant="primary"
            leadingVisual={SearchIcon}
            onClick={handleSearchUserAsync}
          />
        </Box>
        <Box sx={{ color: 'red', fontStyle: 'italic' }}>{!isSearch ? "(Nhấn nút tìm kiếm để tìm dữ liệu)" : ""}</Box>
      </Box>

      <DataTable
        data={dataForExport}
        isLoading={status === ePageBaseStatus.is_loading}
        title="Điểm danh ăn học sinh"
        height={dataForExport.length === 0 ? "" : "calc(100vh - 240px)"}
        exportEnable

        actionComponent={
          <>
            <Box sx={{ marginRight: 2 }}>
              <ComboboxKhoi
                value={filterKhoiId}
                onValueChanged={setFilterKhoiId}
                dm_truong_id={filterTruongId}
              />
            </Box>
            <Box sx={{ marginRight: 2 }}>
              <ComboboxCrmLop
                value={filterLopId}
                onValueChanged={setFilterLopId}
                dm_truong_id={filterTruongId}
                dm_khoi_id={filterKhoiId}
                nam_hoc={nam_hoc}
                isShowClearButton
              />
            </Box>
            <Box sx={{ marginRight: 2 }}>
              <ComboboxTrangThaiDiemDanhAn
                value={statusFilter}
                onValueChanged={(id) => setStatusFilter(id as "all" | "hopLe" | "khongHopLe")}
                isShowClearButton
              />
            </Box>
            <Button
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              onClick={handleSearchUserAsync}
            />
          </>
        }
        columns={[
          {
            caption: "Mã HS",
            dataField: "ma_hs",
            width: 120,
            isMainColumn: true,
          },
          {
            caption: "Học sinh",
            dataField: "ho_ten",
            width: 220,
            cellRender: (data: IDiemDanhAnItemExtended) => (
              <HocSinhShortInfo data={data} />
            ),
          },
          { caption: "Khối", dataField: "ten_khoi", width: 70 },
          { caption: "Lớp", dataField: "ten_lop", width: 70 },
          {
            caption: "Ngày",
            dataField: "ngay_an",
            width: 100,
            cellRender: (data: IDiemDanhAnItemExtended) => {
              return (
                <Box>
                  {data.ngay_an ? moment(data.ngay_an).format("DD/MM/YYYY") : ""}
                </Box>
              )
            }
          },
          { caption: "Mã bán trú", dataField: "combo_dang_ky", width: 100, align: 'center' },
          ...generateCaAnColumns,
        ]}
        paging={{ enable: true, pageSizeItems: [50, 200, 500, 1000, 5000] }}
      />
      {isShowViewFaceId && <FaceidViewModal urlImage={selectedImageUrl} titleModal={selectedTitle} onClose={() => setShowViewFaceId(false)} />}
    </Box>
  );
};

export default BaoCaoDiemDanhPage;